import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { LanguageProvider, useLang } from "@/context/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import "@/App.css";

import AppLayout from "@/components/AppLayout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import NewComplaintPage from "@/pages/NewComplaintPage";
import ComplaintsListPage from "@/pages/ComplaintsListPage";
import ComplaintDetailsPage from "@/pages/ComplaintDetailsPage";
import DepartmentsPage from "@/pages/DepartmentsPage";
import SearchPage from "@/pages/SearchPage";
import SettingsPage from "@/pages/SettingsPage";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready)
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/** After login, sync user's server-side theme+language preferences into local context. */
function PreferencesSync() {
  const { user } = useAuth();
  const { setTheme } = useTheme();
  const { setLang } = useLang();
  const [synced, setSynced] = useState(false);
  useEffect(() => {
    if (!user || synced) return;
    api
      .get("/settings")
      .then(({ data }) => {
        const prefs = data.preferences || {};
        if (prefs.theme) setTheme(prefs.theme);
        if (prefs.language) setLang(prefs.language);
        setSynced(true);
      })
      .catch(() => setSynced(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, synced]);
  return null;
}

function App() {
  // Lock the browser tab title to "RSB" — third-party scripts otherwise
  // rewrite it back to their own branding after page load.
  useEffect(() => {
    const LOCKED = "RSB";
    document.title = LOCKED;
    const titleEl = document.querySelector("title");
    if (!titleEl) return;
    const observer = new MutationObserver(() => {
      if (document.title !== LOCKED) document.title = LOCKED;
    });
    observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <PreferencesSync />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route
                element={
                  <Protected>
                    <AppLayout />
                  </Protected>
                }
              >
                <Route path="/" element={<DashboardPage />} />
                <Route path="/complaints" element={<ComplaintsListPage />} />
                <Route path="/complaints/new" element={<NewComplaintPage />} />
                <Route path="/complaints/:id" element={<ComplaintDetailsPage />} />
                <Route path="/departments" element={<DepartmentsPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster position="top-right" richColors duration={2500} offset={64} />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
